import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useScheduleEditorStore } from "../store/useScheduleEditorStore";
import { useScheduleTagging } from "./useScheduleTagging";
import {
  buildSchedulePayload,
  canAutoTag,
  isRowTypeRequired,
} from "../utils/scheduleEditorMapping";
import {
  fetchSchedulesByType,
  submitScheduleCreation,
  submitTaggingChange,
} from "../services/scheduleEditorService";

export function useScheduleEditor({
  open,
  editMode,
  tcId,
  detailsId,
  row,
  rowIndex,
  reference,
  source,
  section,
  onClose,
  onDone,
  handleError,
  companyType = "common",
}) {
  const {
    tagSchedule,
    createTagSchedule,
    unTagSchedule,
    getPlScheduleByType,
    getCaScheduleByType,
  } = useScheduleTagging();
  const queryClient = useQueryClient();
  const wasOpenRef = useRef(false);
  const store = useScheduleEditorStore();
  const {
    activeTab,
    existingScheduleStep,
    selectedScheduleType,
    selectedSchedule,
    initialTargetSchedule,
    isRowTypeDisabled,
    autoTaggingModeOn,
    isSubmitting,
    initialize,
    reset,
    setActiveTab,
    setExistingScheduleStep,
    setSelectedSchedule,
    setAutoTaggingModeOn,
    setSubmitting,
  } = store;

  useEffect(() => {
    if (open && row) {
      initialize({ row, editMode });
      return;
    }

    if (!open) reset();
  }, [open, row, editMode, initialize, reset]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      queryClient.removeQueries({ queryKey: ["schedule-editor", "schedules"] });
    }

    wasOpenRef.current = open;
  }, [open, queryClient]);

  const schedulesQuery = useQuery({
    queryKey: [
      "schedule-editor",
      "schedules",
      tcId,
      detailsId,
      selectedScheduleType,
      companyType,
    ],
    queryFn: () =>
      fetchSchedulesByType({
        tcId,
        detailsId,
        selectedScheduleType,
        companyType,
        getPlScheduleByType,
        getCaScheduleByType,
      }),
    enabled: open && Boolean(selectedScheduleType) && Boolean(getPlScheduleByType),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const schedules = schedulesQuery.data || [];
  const isFetchingSchedules = schedulesQuery.isFetching;
  const isInitialLoading =
    schedulesQuery.isLoading ||
    (schedulesQuery.isFetching && schedulesQuery.data === undefined);

  useEffect(() => {
    if (schedulesQuery.isError) {
      handleError("Unexpected issue happens. Please try again later.");
    }
  }, [schedulesQuery.isError, handleError]);

  useEffect(() => {
    setAutoTaggingModeOn(
      canAutoTag(selectedScheduleType) &&
        !editMode &&
        schedules.length === 1 &&
        !schedulesQuery.isError
    );
  }, [
    selectedScheduleType,
    editMode,
    schedules.length,
    schedulesQuery.isError,
    setAutoTaggingModeOn,
  ]);

  const rowTypeRequired = isRowTypeRequired(selectedScheduleType, companyType);
  const isSameSchedule =
    editMode && row?.schedule && selectedSchedule.sch_id === row.schedule.sch_id;
  const recommendedSchedule = schedules.length > 1 ? schedules[0] : null;
  const autoMatchedSchedule = autoTaggingModeOn ? schedules[0] : null;

  useEffect(() => {
    if (!open || autoTaggingModeOn || !recommendedSchedule) return;
    if (selectedSchedule.sch_id !== null) return;

    setSelectedSchedule({
      sch_id: recommendedSchedule.sch_id,
      scheduleTitle: recommendedSchedule.scheduleTitle,
      scheduleNo: recommendedSchedule.scheduleNo,
    });
  }, [
    open,
    autoTaggingModeOn,
    recommendedSchedule,
    selectedSchedule.sch_id,
    setSelectedSchedule,
  ]);

  const createPayload = useCallback(
    (scheduleId = null, targetSchedule) =>
      buildSchedulePayload({
        tcId,
        detailsId,
        scheduleId,
        row,
        rowIndex,
        section,
        selectedScheduleType,
        targetSchedule,
        reference,
        companyType,
      }),
    [
      tcId,
      detailsId,
      row,
      rowIndex,
      section,
      selectedScheduleType,
      reference,
      companyType,
    ]
  );

  const taggingMutation = useMutation({
    mutationFn: ({ data, refObj }) =>
      submitTaggingChange({
        data,
        reference: refObj,
        editMode,
        source,
        tagSchedule,
        unTagSchedule,
      }),
    onMutate: () => setSubmitting(true),
    onSuccess: (updatedRow, { refObj }) => onDone(updatedRow, refObj),
    onError: (error) => handleError(error),
    onSettled: () => {
      setSubmitting(false);
      onClose();
    },
  });

  const creationMutation = useMutation({
    mutationFn: ({ data, refObj }) =>
      submitScheduleCreation({
        data,
        reference: refObj,
        editMode,
        source,
        createTagSchedule,
        unTagSchedule,
      }),
    onMutate: () => setSubmitting(true),
    onSuccess: (updatedRow, { refObj }) => onDone(updatedRow, refObj),
    onError: (error) => handleError(error),
    onSettled: () => {
      setSubmitting(false);
      onClose();
    },
  });

  const confirmExistingSchedule = useCallback((formValues) => {
    const targetSchedule = {
      scheduleTitle:
        formValues.scheduleTitle || selectedSchedule.scheduleTitle || "",
      rowType: formValues.rowType || "",
    };
    const scheduleForSubmit = autoMatchedSchedule || selectedSchedule;

    if (
      !autoMatchedSchedule &&
      activeTab === 0 &&
      existingScheduleStep === 0 &&
      rowTypeRequired
    ) {
      if (scheduleForSubmit.sch_id !== null) setExistingScheduleStep(1);
      return;
    }

    if (scheduleForSubmit.sch_id === null) return;
    if (rowTypeRequired && !targetSchedule.rowType.trim()) return;

    if (isSameSchedule) {
      handleError("You are already tagged to this schedule. No changes needed.");
      return;
    }

    const { rowMap, dataMap, refObj, scheduleType } = createPayload(
      scheduleForSubmit.sch_id,
      targetSchedule
    );
    const data = {
      ...dataMap,
      rows: rowMap[scheduleType],
    };

    taggingMutation.mutate({
      data,
      refObj: { ...refObj, schedule: scheduleForSubmit },
    });
  }, [
    activeTab,
    existingScheduleStep,
    rowTypeRequired,
    isSameSchedule,
    createPayload,
    selectedSchedule,
    autoMatchedSchedule,
    setExistingScheduleStep,
    handleError,
    taggingMutation,
  ]);

  const createNewSchedule = useCallback((formValues) => {
    const targetSchedule = {
      scheduleTitle: formValues.scheduleTitle || "",
      rowType: formValues.rowType || "",
    };
    const { rowMap, dataMap, refObj, scheduleType } = createPayload(
      null,
      targetSchedule
    );
    const data = {
      ...targetSchedule,
      ...dataMap,
      scheduleType,
      rows: rowMap[scheduleType],
    };

    creationMutation.mutate({ data, refObj });
  }, [
    createPayload,
    creationMutation,
  ]);

  const isBusy = isSubmitting;

  return {
    activeTab,
    existingScheduleStep,
    selectedScheduleType,
    selectedSchedule,
    initialTargetSchedule,
    schedules,
    recommendedSchedule,
    autoMatchedSchedule,
    isRowTypeDisabled,
    autoTaggingModeOn,
    rowTypeRequired,
    isSameSchedule,
    isInitialLoading,
    isFetchingSchedules,
    isBusy,
    setActiveTab,
    setExistingScheduleStep,
    setSelectedSchedule,
    confirmExistingSchedule,
    createNewSchedule,
  };
}
